import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { config } from '@abyss/web/tools/config';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { find } from 'lodash';
import React, { useEffect } from 'react';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../common/Utils';
import { replaceSpaceToUpperCase } from '../../../common/Utils/replaceString';
import { useLagoon } from '../../../hooks/useLagoon';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import { phoneOnly } from '../../ConstantsStyles';
import {
  StyledContentWrapper,
  StyledDescriptionText,
  StyledDynamicContent,
  StyledFlexContainer,
  StyledLearnMoreButton,
  StyledListItemText,
  StyledMaybeLaterButton,
  StyledSubtitleText,
  StyledTitleText,
} from './IntroModal.styled';
import { Modal } from './Modal';
import { FlexContainer } from './SmartChoice.styled';

export interface Props {
  isOpen: boolean;
  handleCloseDrawer: () => void;
  handleLearnAboutSC: () => void;
  handleMaybeLater?: () => void;
}

export const INTRO_SMART_CHOICE = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/introSmartChoiceModal.png`;

export const INTRO_SMART_CHOICE_MOBILE_VIEW = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/introSmartChoiceModalMobileView.png`;

export const IntroModal = ({
  isOpen,
  handleCloseDrawer,
  handleLearnAboutSC,
  handleMaybeLater,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const SMART_CHOICE_INTRO_POP_UP_TITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_TITLE,
  });
  const SMART_CHOICE_INTRO_POP_UP_SUBTITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_SUBTITLE,
  });
  const SMART_CHOICE_INTRO_POP_UP_DESCRIPTION = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_DESCRIPTION,
  });
  const SMART_CHOICE_INTRO_POP_UP_TEXT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_TEXT,
  });
  const SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT,
  });
  const SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER,
  });
  const SMART_CHOICE_INTRO_POP_UP_SIMPLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_SIMPLE,
  });
  const SMART_CHOICE_INTRO_POP_UP_CUSTOMIZED = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_CUSTOMIZED,
  });
  const SMART_CHOICE_INTRO_POP_UP_DATA_DRIVEN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_DATA_DRIVEN,
  });
  const enableAdobeAnalytic = getFeatureFlag(
    featureFlags,
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NEW_UX_SMART_CHOICE_ANALYTICS
  );
  const subtitle = SMART_CHOICE_INTRO_POP_UP_SUBTITLE?.message;

  useEffect(() => {
    if (!isOpen || !subtitle || !enableAdobeAnalytic) return;
    adobeModalTrackEvent(subtitle);
  }, [isOpen, subtitle]);

  const ListItems = ({ heading, leftPadding = false }) => (
    <StyledFlexContainer leftPadding={leftPadding}>
      <IconSymbol
        color="$success1"
        icon="check"
        size={mobileScreen ? '20px' : '24px'}
      />
      <StyledListItemText
        data-testid={`heading-${replaceSpaceToUpperCase(heading)}`}
        mobileScreen={mobileScreen}
      >
        {heading}
      </StyledListItemText>
    </StyledFlexContainer>
  );

  return (
    <React.Fragment>
      <Modal
        aria-describedby="smart-choice-intro-modal-description"
        aria-label="Smart Choice Intro Modal"
        aria-labelledby="smart-choice-intro-modal-title"
        css={{
          'abyss-modal-header-container': { display: 'none' },
        }}
        data-testid="intro-modal"
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        title="Smart Choice"
      >
        <StyledContentWrapper
          data-testid="intro-modal-content"
          mobileScreen={mobileScreen}
        >
          <StyledTitleText aria-labelledby="smart-choice-intro-modal-title">
            {SMART_CHOICE_INTRO_POP_UP_TITLE?.message}
          </StyledTitleText>
          <img
            alt="Smart Choice 82% example, information convenient and personalized"
            src={
              mobileScreen ? INTRO_SMART_CHOICE_MOBILE_VIEW : INTRO_SMART_CHOICE
            }
            style={{ paddingTop: mobileScreen ? '20px' : '15px' }}
          />
          <StyledSubtitleText data-testid="intro-modal-subtitle">
            {subtitle}
          </StyledSubtitleText>
          <StyledDescriptionText>
            {SMART_CHOICE_INTRO_POP_UP_DESCRIPTION?.message}
          </StyledDescriptionText>
          <StyledDynamicContent
            dangerouslySetInnerHTML={{
              __html: SMART_CHOICE_INTRO_POP_UP_TEXT?.message,
            }}
            mobileScreen={mobileScreen}
          />
          {!mobileScreen ? (
            <FlexContainer css={{ marginTop: '4px', marginBottom: '5px' }}>
              <ListItems heading={SMART_CHOICE_INTRO_POP_UP_SIMPLE?.message} />
              <ListItems
                heading={SMART_CHOICE_INTRO_POP_UP_CUSTOMIZED?.message}
                leftPadding
              />
              <ListItems
                heading={SMART_CHOICE_INTRO_POP_UP_DATA_DRIVEN?.message}
                leftPadding
              />
            </FlexContainer>
          ) : (
            <>
              <ListItems heading={SMART_CHOICE_INTRO_POP_UP_SIMPLE.message} />
              <ListItems
                heading={SMART_CHOICE_INTRO_POP_UP_CUSTOMIZED?.message}
              />
              <ListItems
                heading={SMART_CHOICE_INTRO_POP_UP_DATA_DRIVEN?.message}
              />
            </>
          )}
          <StyledLearnMoreButton
            data-testid="learn-more-about"
            onClick={handleLearnAboutSC}
          >
            {SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT?.message}
          </StyledLearnMoreButton>
          <StyledMaybeLaterButton
            data-testid="maybe-later-sc"
            mobileScreen={mobileScreen}
            onClick={handleMaybeLater}
            variant="outline"
          >
            {SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER?.message}
          </StyledMaybeLaterButton>
        </StyledContentWrapper>
      </Modal>
    </React.Fragment>
  );
};
